import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTable, usePagination } from "react-table";
import axiosApi from "../Utils/axiosApi";
import HeaderMenu from "../Header/HeaderMenu";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Typography,
   Grid,
  Chip,
  Box,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import EventIcon from "@mui/icons-material/Event";
import "./UserListPage.css";

const HostelOwnerEnquiryListPage = () => {
  const [data, setData] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      const response = await axiosApi.post(
        "/hostelOwnerEnquiry/getHostelOwnerEnquiry",
        {}
      );
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to load enquiries:", error);
    }
  };

  const handleView = useCallback((enquiry) => {
    setSelectedEnquiry(enquiry);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEnquiry(null);
  };

  const columns = useMemo(
    () => [
      { Header: "Hostel / PG Name", accessor: "hostelName" },
      { Header: "Owner Name", accessor: "ownerName" },
      { Header: "Mobile", accessor: "mobileNumber" },
      { Header: "Email", accessor: "email" },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleView(row.original)}
          >
            View
          </Button>
        ),
      },
    ],
    [handleView]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    pageCount,
    state: { pageIndex, pageSize },
    setPageSize,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 5 },
    },
    usePagination
  );

  return (
    <>
      <HeaderMenu />

      <div className="user-list">
        <h2>Hostel Owner Enquiries</h2>

        <div className="table-container">
          <table {...getTableProps()} className="table">
            <thead>
              {headerGroups.map((hg) => (
                <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                  {hg.headers.map((col) => (
                    <th {...col.getHeaderProps()} key={col.id}>
                      {col.render("Header")}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={row.id}>
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        key={cell.column.id}
                        data-label={cell.column.Header}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "16px",
          }}
        >
          <Pagination
            count={pageCount}
            page={pageIndex + 1}
            onChange={(e, value) => gotoPage(value - 1)}
            color="primary"
            shape="rounded"
          />

          <div>
            <label style={{ marginRight: 8 }}>Rows per page:</label>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[5, 10, 15, 20].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* View Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={(e, reason) => {
          if (reason !== "backdropClick") handleCloseDialog();
        }}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            backgroundColor: "#f5f7fa",
          },
        }}
      >
        {/* Title */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 700,
            borderBottom: "1px solid #e5e7eb",
            px: 4,
          }}
        >
          Enquiry Details

          <Chip
            label={selectedEnquiry?.status || "NEW"}
            color={
              selectedEnquiry?.status === "CONTACTED"
                ? "success"
                : "primary"
            }
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{ px: 4, py: 3 }}>
          {selectedEnquiry && (
            <>
              {/* Meta */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                  color: "text.secondary",
                }}
              >
                <EventIcon fontSize="small" />
                <Typography variant="body2">
                  Created on:{" "}
                  {selectedEnquiry.createdAt
                    ? new Date(
                        selectedEnquiry.createdAt
                      ).toLocaleString()
                    : "-"}
                </Typography>
              </Box>

              {/* Info Card */}
              <Box
                sx={{
                  backgroundColor: "#fff",
                  p: 3,
                  borderRadius: 2,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                  mb: 3,
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Hostel / PG Name
                    </Typography>
                    <Typography fontWeight={600}>
                      {selectedEnquiry.hostelName}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Owner Name
                    </Typography>
                    <Typography fontWeight={600}>
                      {selectedEnquiry.ownerName}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Mobile Number
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <PhoneIcon fontSize="small" color="action" />
                      <Typography fontWeight={600}>
                        {selectedEnquiry.mobileNumber}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Email Address
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <EmailIcon fontSize="small" color="action" />
                      <Typography fontWeight={600}>
                        {selectedEnquiry.email}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Total Beds
                    </Typography>
                    <Typography fontWeight={600}>
                      {selectedEnquiry.totalBeds || "-"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Message */}
              <Box
                sx={{
                  backgroundColor: "#fff",
                  p: 3,
                  borderRadius: 2,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Message
                </Typography>

                <Typography
                  sx={{
                    mt: 1,
                    minHeight: 80,
                    backgroundColor: "#f9fafb",
                    p: 2,
                    borderRadius: 1,
                    border: "1px solid #e5e7eb",
                  }}
                >
                  {selectedEnquiry.message || "-"}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={{
            px: 4,
            py: 2,
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "#fff",
          }}
        >
          <Button
            onClick={handleCloseDialog}
            variant="contained"
            size="large"
            fullWidth
            sx={{ fontWeight: 600, borderRadius: 2 }}
          >
            CLOSE
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HostelOwnerEnquiryListPage;
